import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { EstensaIconService, Logger, RouterStackService } from '@se/se.ui.html5';
import {
    CultureService,
    ThemeService,
    EstensaBusWrapperService,
    EstensaWaitingService,
    RootState,
    sePdfModalToggle,
    seIsIntegrationAppConnected,
    seSignalrConnected,
    seLogin,
    selectSeButtonsToolbarRightSlider,
    seOpenTooluploadDialog,
    selectSeButtonsTimeline
} from '@se/platform';
import { Observable, filter, delay, map } from 'rxjs';

interface RightSliderState {
    isClosed: boolean;
    isVisible: boolean;
    overlap: boolean;
    overlapOpen: boolean;
}

interface TimelineState {
    open : boolean;
    active : boolean;
}

@Component({
    selector: 'host-hybrid-root',
    templateUrl: './host-hybrid.component.html',
    styleUrls: ['./host-hybrid.component.scss'],
})
export class HostHybridComponent implements AfterViewInit, OnDestroy, OnInit {
    currentEstensaTheme: Observable<string>;
    isVNext: boolean = false;
    isNg2: boolean = false;

    isWaiting$ = this.waitingService.isWaiting$.pipe(delay(0));

    rightSlider$: Observable<RightSliderState> = this.store.pipe(
        select(selectSeButtonsToolbarRightSlider),
        map((slider) => ({
            isClosed: slider.enabled && !slider.checked,
            isVisible: slider.enabled,
            overlap: slider.enabled && !slider.checked && slider.overlap,
            overlapOpen: slider.enabled && slider.checked && slider.overlap,
        })),
    );

    timelineOpen$ : Observable<TimelineState> = this.store.pipe(
        select(selectSeButtonsTimeline),
        map((timeline) =>({
            open: timeline.open,
            active: timeline.active
        })),
    );

    openvna: boolean = false;

    pdfModalConfig$ = this.store.pipe(select('pdfModal'));

    // constructor

    constructor(
        private cultureService: CultureService,
        private elementRef: ElementRef,
        private estensaBusWrapperService: EstensaBusWrapperService,
        private estensaIconService: EstensaIconService,
        private logger: Logger,
        private store: Store<RootState>,
        private router: Router,
        private routerStackService: RouterStackService,
        private themeService: ThemeService,
        private translateService: TranslateService,
        private waitingService: EstensaWaitingService,
        private zone: NgZone,
    ) {
        // DGS_AP - nuova azione o modificare stato per aggiornare 'se-external-component-container width'
    }

    // public functions
    ngOnDestroy() {
        this.isVNext = false;
        this.isNg2 = false;
    }

    ngOnInit() {
        this.setCulture();
        this.initTheme();
        this.initIcons();
        this.estensaBusWrapperService.busDataFired.subscribe((message) =>
            this.zone.run(() => this.handleBusMessage(message)),
        );

        this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
            if (e.url != null) {
                // Verifico se è una pagina Angular2 e\o una VNext
                this.isNg2 = e.url.startsWith('/new/') ? true : false;
                this.isVNext = e.url.startsWith('/new/vnext') ? true : false;
                this.updateLayout();
                this.syncNg1();
            }
        });

        this.router.initialNavigation();
    }

    onPdfModalVisiblityChange(visible: boolean) {
        this.store.dispatch(sePdfModalToggle({ visible }));
    }

    ngAfterViewInit() {
        this.waitingService.isWaiting = false; // Se ci fosse qualche wait la chiudo
    }

    // private functions

    private updateLayout() {
        this.elementRef.nativeElement.setAttribute('style', this.isNg2 ? 'display:flex;' : 'display:none;');

        let ebitBodyApp = document.getElementById('ebitBodyApp');
        let footer = document.getElementById('footer');

        let header = document.getElementById('header'); // VNext
        let middleViewArea = document.getElementById('middleViewArea'); // VNext
        let headerText = document.getElementById('title-and-patient-row-container'); // VNext

        if (this.isNg2) {
            // Anugular2
            footer.classList.add('new-layout-display-none');

            if (this.isVNext) {
                // VNext
                ebitBodyApp.classList.add('vnext-layout-content');
                header.classList.add('new-layout-display-none');
                middleViewArea.classList.add('new-layout-middle-area');
                headerText.classList.add('new-layout-display-none');
            } else {
                ebitBodyApp.classList.add('new-layout-content');
            }
        } else {
            // Classic
            footer.classList.remove('new-layout-display-none');
            ebitBodyApp.classList.remove('vnext-layout-content');
            header.classList.remove('new-layout-display-none');
            middleViewArea.classList.remove('new-layout-middle-area');
            ebitBodyApp.classList.remove('new-layout-content');
            headerText.classList.remove('new-layout-display-none');
        }
    }

    private initIcons() {
        this.estensaIconService.initEstensaIcons();
    }

    private initTheme() {
        this.currentEstensaTheme = this.themeService.getTheme();
    }

    private handleBusMessage(message) {
        switch (message.messageType) {
            case 'IS_INTEGRATION_CONNECTION':
                this.store.dispatch(seIsIntegrationAppConnected({ isConnected: message.data }));
                break;
            case 'ON_LOGIN_MESSAGE':
                this.store.dispatch(seLogin({ sso: message.data.sso }));
                break;
            case 'ROUTER_NAVIGATION_REQUEST':
                this.routerStackService.go(message.data.url, message.data.params, undefined, { push: false });
                break;
            case 'SIGNALR_CONNECTION':
                this.store.dispatch(seSignalrConnected({ isConnected: message.data }));
                break;
            case 'ESTENSA_TOOL_VNA':
                this.zone.run(() => {
                    this.store.dispatch(seOpenTooluploadDialog(message));
                });
                break;
        }
    }

    private setCulture() {
        this.cultureService.initializeAsync().then(() => {
            const language = this.cultureService.getCulture();

            this.translateService.use(language);
            this.logger.debug('translateService uses ' + language);
        });
    }

    private syncNg1() {
        this.estensaBusWrapperService.notify({ messageType: 'NG2_APPLY_ASYNC' });
    }
}

export enum SeHtmlPageType {
    Unknown,
    Classic,
    Angular,
    VNext,
}
